// Enhanced Benchmark Entry Entity with Hierarchy Integration
// This version dynamically loads hierarchy options

import { useMemo } from 'react';
import { EntityDefinition } from '@/types/crud';
import { useHierarchyOptions } from '@/hooks/useHierarchyOptions';

export const createBenchmarkEntryEntity = (): EntityDefinition => {
  return {
    name: 'Benchmark Entry',
    displayName: 'Benchmark Entries',
    displayNameSingular: 'Benchmark Entry',
    apiEndpoint: '/benchmarking',
    key: 'benchmark_entries',
    fields: [
      {
        key: 'location',
        label: 'Location',
        type: 'select',
        required: true,
        order: 1,
        options: [], // Will be populated dynamically
        optionsValueField: 'value',
        optionsLabelField: 'label'
      },
      {
        key: 'function_name',
        label: 'Function',
        type: 'select',
        required: true,
        order: 2,
        options: [], // Will be populated dynamically
        optionsValueField: 'value',
        optionsLabelField: 'label'
      },
      {
        key: 'business',
        label: 'Business',
        type: 'select',
        required: true,
        order: 3,
        options: [], // Will be populated dynamically
        optionsValueField: 'value',
        optionsLabelField: 'label'
      },
      {
        key: 'subprocess',
        label: 'Subprocess',
        type: 'select',
        required: false,
        order: 4,
        options: [], // Will be populated dynamically based on function selection
        optionsValueField: 'value',
        optionsLabelField: 'label',
        dependsOn: 'function_name'
      },
      {
        key: 'cost_bucket',
        label: 'Cost Bucket',
        type: 'select',
        required: true,
        order: 5,
        options: [
          { value: 'labor', label: 'Labor Costs' },
          { value: 'outsourcing', label: 'Outsourcing Costs' },
          { value: 'technology', label: 'Technology Costs' },
          { value: 'raw_material', label: 'Raw Material Costs' },
          { value: 'transportation', label: 'Transportation Costs' },
          { value: 'occupancy', label: 'Occupancy Costs' }
        ]
      },
      {
        key: 'cost_amount',
        label: 'Cost Amount (total)',
        type: 'number',
        required: false,
        order: 6,
        minValue: 0
      },
      {
        key: 'benchmark_process_id',
        label: 'Process',
        type: 'select',
        required: false,
        order: 6.5,
        options: [],
        optionsEndpoint: '/admin/process-admin/processes',
        optionsValueField: 'id',
        optionsLabelField: 'name'
      },
      {
        key: 'benchmark_subprocess_id',
        label: 'Subprocess',
        type: 'select',
        required: false,
        order: 6.6,
        options: [],
        optionsEndpoint: '/admin/process-admin/processes',
        optionsValueField: 'id',
        optionsLabelField: 'name',
        dependsOn: 'benchmark_process_id'
      },
      {
        key: 'labor_amount',
        label: 'Labor amount',
        type: 'number',
        required: false,
        order: 14,
        minValue: 0
      },
      {
        key: 'outsourcing_amount',
        label: 'Outsourcing amount',
        type: 'number',
        required: false,
        order: 15,
        minValue: 0
      },
      {
        key: 'technology_amount',
        label: 'Technology amount',
        type: 'number',
        required: false,
        order: 16,
        minValue: 0
      },
      {
        key: 'material_amount',
        label: 'Material amount',
        type: 'number',
        required: false,
        order: 17,
        minValue: 0
      },
      {
        key: 'transportation_amount',
        label: 'Transportation amount',
        type: 'number',
        required: false,
        order: 18,
        minValue: 0
      },
      {
        key: 'occupancy_amount',
        label: 'Occupancy amount',
        type: 'number',
        required: false,
        order: 19,
        minValue: 0
      },
      {
        key: 'other_amount',
        label: 'Other amount',
        type: 'number',
        required: false,
        order: 20,
        minValue: 0
      },
      {
        key: 'cost_notes',
        label: 'Cost notes',
        type: 'textarea',
        required: false,
        order: 21
      },
      {
        key: 'revenue',
        label: 'Revenue',
        type: 'number',
        required: false,
        order: 22,
        minValue: 0
      },
      {
        key: 'customer_satisfaction',
        label: 'Customer satisfaction (1–10)',
        type: 'number',
        required: false,
        order: 23,
        minValue: 1,
        maxValue: 10
      },
      {
        key: 'innovation_count',
        label: 'Innovation count',
        type: 'number',
        required: false,
        order: 24,
        minValue: 0
      },
      {
        key: 'quality_pct',
        label: 'Quality %',
        type: 'number',
        required: false,
        order: 25,
        minValue: 0,
        maxValue: 100
      },
      {
        key: 'speed_value',
        label: 'Speed value',
        type: 'number',
        required: false,
        order: 26,
        minValue: 0
      },
      {
        key: 'effectiveness_notes',
        label: 'Effectiveness notes',
        type: 'textarea',
        required: false,
        order: 27
      },
      {
        key: 'complexity_value',
        label: 'Complexity value',
        type: 'number',
        required: false,
        order: 28,
        minValue: 0
      },
      {
        key: 'complexity_notes',
        label: 'Complexity notes',
        type: 'textarea',
        required: false,
        order: 29
      },
      {
        key: 'entry_notes',
        label: 'Entry notes',
        type: 'textarea',
        required: false,
        order: 30
      },
      {
        key: 'cost_driver',
        label: 'Cost Driver',
        type: 'text',
        required: false,
        order: 7
      },
      {
        key: 'cost_driver_value',
        label: 'Cost Driver Value',
        type: 'number',
        required: false,
        order: 8,
        minValue: 0
      },
      {
        key: 'cost_driver_unit',
        label: 'Cost Driver Unit',
        type: 'text',
        required: false,
        order: 9
      },
      {
        key: 'fte_count',
        label: 'FTE Count',
        type: 'number',
        required: false,
        order: 10,
        minValue: 0
      },
      {
        key: 'footprint_sq_ft',
        label: 'Footprint (sq ft)',
        type: 'number',
        required: false,
        order: 11,
        minValue: 0
      },
      {
        key: 'is_validated',
        label: 'Validated',
        type: 'boolean',
        required: false,
        order: 12
      },
      {
        key: 'validation_notes',
        label: 'Validation Notes',
        type: 'textarea',
        required: false,
        order: 13
      }
    ],
    displayFields: ['location', 'function_name', 'business', 'cost_bucket', 'cost_amount', 'labor_amount', 'revenue', 'customer_satisfaction', 'fte_count'],
    searchFields: ['location', 'function_name', 'business', 'subprocess'],
    sortField: 'created_at',
    sortOrder: 'desc'
  };
};

// Hook to get the entity with populated hierarchy options
export const useBenchmarkEntryEntity = (): EntityDefinition => {
  const { businessOptions, functionalOptions, geographicOptions, subprocessOptions } = useHierarchyOptions();
  
  return useMemo(() => {
    const entity = createBenchmarkEntryEntity();
    
    // Update the entity with hierarchy options
    const locationField = entity.fields.find(f => f.key === 'location');
    if (locationField) {
      locationField.options = geographicOptions;
    }
    
    const functionField = entity.fields.find(f => f.key === 'function_name');
    if (functionField) {
      functionField.options = functionalOptions;
    }
    
    const businessField = entity.fields.find(f => f.key === 'business');
    if (businessField) {
      businessField.options = businessOptions;
    }
    
    const subprocessField = entity.fields.find(f => f.key === 'subprocess');
    if (subprocessField) {
      subprocessField.options = subprocessOptions;
    }
    
    return entity;
  }, [businessOptions, functionalOptions, geographicOptions, subprocessOptions]);
};
